import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { formValues, schema } from "./validations";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BUDGET_NAME, NOTES, VERSION_NO, YEAR } from "./consts";
import { useQueryClient } from "@tanstack/react-query";
import { useCopyBudgetMutation } from "@/services";
import { toast } from "@/components/Toast";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useCopyBudget = ({
  setShowCopyDialog,
  copyVersionDetails,
}: {
  setShowCopyDialog: React.Dispatch<React.SetStateAction<boolean>>;
  copyVersionDetails: any;
}) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const form = useForm<formValues>({
    defaultValues: {
      [BUDGET_NAME]: "",
      [VERSION_NO]: "",
      [YEAR]: "",
      [NOTES]: "",
    },
    resolver: zodResolver(schema),
  });
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const communityId = (id || communityDetail?.communityId) as string;

  useEffect(() => {
    form.reset({
      [BUDGET_NAME]: "",
      [VERSION_NO]: "",
      [YEAR]: "",
      [NOTES]: "",
    });
  }, []);

  const requiredFields = getRequiredFields(schema);

  const { mutate: copyInfo, isPending: isCopyPending } = useCopyBudgetMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get-budget-data", communityId],
      });
      onCloseDialog();
      toast.success("Budget Copied Successfully");
    },
  });

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);
    copyInfo({ communityId: communityId as string, budgetId: copyVersionDetails?.budgetId as string, data: _data });
  };

  const onCloseDialog = () => {
    setShowCopyDialog && setShowCopyDialog(false);
    form.reset();
  };

  return {
    form,
    requiredFields,
    isCopyPending,
    onCloseDialog,
    onSubmit,
  };
};

export default useCopyBudget;
